import { type Menu, type ScheduleWindow, isValidWindowTime } from '../lib';

/**
 * The menu wizard with no React in it.
 *
 * Everything that decides *whether the user may move on* and *what goes on the
 * wire* lives here, because both are the parts that break silently. A step
 * guard that lives inside a `disabled` prop can only be checked by rendering
 * four screens; the request body assembled inline in a submit handler can only
 * be checked by watching the network tab. Both are one exported function here,
 * and `wizardState.test.ts` reads them directly.
 */

export const STEPS = ['basic', 'schedule', 'items', 'review'] as const;
export type StepId = (typeof STEPS)[number];

/**
 * The same four steps, seeded differently and submitted differently.
 *
 * Create is one POST plus a bulk attach, chunked. Edit is a PATCH plus a diff
 * fed through the same chunking. The diff is the part that has to be computed
 * here rather than in a handler: it decides how many bulk requests go out, and
 * it is the thing a retry has to be able to recompute after a batch is refused.
 */
export type WizardMode = 'create' | 'edit';

/**
 * Branch-scoped or tenant-wide — the axis the *code* has.
 *
 * The design draws this radio as Branch menu / **Area menu**, which is neither
 * of these. `Menu` is scoped by `tenantId` + optional `branchId` (schema.prisma
 * :1182-1183); `Area` has no menu relation, and area-scoped catalog behaviour
 * was deleted outright in backend `7978703` after the owner refused it. The
 * axis that does exist is the one `resolveActiveMenu` reads: a branch menu, or
 * the `branchId: null` menu it falls back to when no branch menu matches. Drop
 * the second radio and the wizard can no longer produce a guest fallback, so
 * the radio keeps its shape and changes its second option.
 */
export type MenuScope = 'branch' | 'tenant';

export interface DraftItem {
  itemId: string;
  isVisible: boolean;
}

export interface MenuDraft {
  name: string;
  scope: MenuScope;
  branchId: string;
  isActive: boolean;
  /** Which menu a guest QR lands on. `clearOtherDefaults` makes it exclusive. */
  isDefault: boolean;
  /**
   * Derived, never stored. There is no `alwaysAvailable` column: the server
   * reads an absent or empty schedule as open (`menu-availability.ts:45`), so
   * ON means "send `schedule: []`". The windows survive the toggle in memory
   * so turning it off again does not cost the operator their typing.
   */
  alwaysAvailable: boolean;
  schedule: ScheduleWindow[];
  /** Ordered — the index is the `sortOrder` that gets written. */
  items: DraftItem[];
}

/** A sensible first window: the working week, one working day. */
export const DEFAULT_WINDOW: ScheduleWindow = { days: [1, 2, 3, 4, 5], from: '08:00', to: '16:00' };

export const BLANK_DRAFT: MenuDraft = {
  name: '',
  scope: 'branch',
  branchId: '',
  isActive: true,
  isDefault: false,
  alwaysAvailable: true,
  schedule: [],
  items: [],
};

/**
 * Why the wizard will not move on — as a code, so the guard is testable without
 * an i18n instance and the caller owns the wording.
 */
export type Blocker = 'nameRequired' | 'branchRequired' | 'windowNeedsDay' | 'windowNeedsTimes';

/** The schedule half of the guard, shared by step 2 and by submit. */
function scheduleBlocker(draft: MenuDraft): Blocker | null {
  if (draft.alwaysAvailable) return null;
  // `MenuScheduleWindowDto` marks `days` @ArrayNotEmpty and matches both times
  // against `HH:mm`; an emptied `<input type="time">` reads back as `''`.
  if (draft.schedule.some((window) => window.days.length === 0)) return 'windowNeedsDay';
  if (draft.schedule.some((w) => !isValidWindowTime(w.from) || !isValidWindowTime(w.to)))
    return 'windowNeedsTimes';
  return null;
}

/**
 * What stops `Next` on this step, or null when nothing does.
 *
 * Step 3 never blocks: a menu with no items is a legal menu, and saying so on
 * the review step where the consequence is visible beats a dead Next button on
 * a screen that cannot explain itself.
 */
export function blockerFor(step: StepId, draft: MenuDraft): Blocker | null {
  if (step === 'basic') {
    if (!draft.name.trim()) return 'nameRequired';
    if (draft.scope === 'branch' && !draft.branchId) return 'branchRequired';
    return null;
  }
  if (step === 'schedule') return scheduleBlocker(draft);
  return null;
}

/**
 * What stops the create.
 *
 * Re-run over every step rather than trusting that the user walked through
 * them, because the review step's `Edit` links jump backwards and a field can
 * be emptied after its own step was passed.
 */
export function submitBlocker(draft: MenuDraft): Blocker | null {
  return blockerFor('basic', draft) ?? blockerFor('schedule', draft);
}

export function stepIndex(step: StepId): number {
  return STEPS.indexOf(step);
}

export function nextStep(step: StepId): StepId {
  return STEPS[Math.min(stepIndex(step) + 1, STEPS.length - 1)];
}

export function prevStep(step: StepId): StepId {
  return STEPS[Math.max(stepIndex(step) - 1, 0)];
}

/**
 * Exactly what `CreateMenuDto` declares (`create-menu.dto.ts:36-66`).
 *
 * Named as a constant so the test can assert the body against it rather than
 * against a second copy of the list. `main.ts:34-36` runs the global pipe with
 * `forbidNonWhitelisted: true`, so one extra key here is not a dropped value —
 * it is a 400 that takes the whole create down. The three the design draws and
 * this list does not carry are `description`, the area target, and the seasonal
 * `startsOn`/`endsOn`.
 */
export const CREATE_MENU_FIELDS = ['name', 'branchId', 'schedule', 'isDefault', 'isActive'] as const;

export interface CreateMenuBody {
  name: string;
  branchId?: string;
  schedule: ScheduleWindow[];
  isDefault: boolean;
  isActive: boolean;
}

/**
 * The draft as `POST /menus` will take it.
 *
 * `schedule` is always sent, including as `[]`: that empty array is how
 * "always available" is expressed, and omitting it on an edit would leave a
 * previous schedule in place. A tenant-wide menu omits `branchId` entirely
 * rather than sending `''`, which `@IsString()` would accept and the service
 * would then treat as a branch id that matches nothing.
 */
export function createMenuBody(draft: MenuDraft): CreateMenuBody {
  const body: CreateMenuBody = {
    name: draft.name.trim(),
    schedule: draft.alwaysAvailable ? [] : draft.schedule,
    isDefault: draft.isDefault,
    isActive: draft.isActive,
  };
  if (draft.scope === 'branch' && draft.branchId) body.branchId = draft.branchId;
  return body;
}

/** Exactly what `AttachMenuItemDto` declares (`menu-item.dto.ts:4-20`). */
export interface AttachItemBody {
  itemId: string;
  sortOrder: number;
  isVisible: boolean;
}

/** One attach call per selected row; the list position is the `sortOrder`. */
export function attachItemBodies(draft: MenuDraft): AttachItemBody[] {
  return draft.items.map((row, index) => ({
    itemId: row.itemId,
    sortOrder: index,
    isVisible: row.isVisible,
  }));
}

/**
 * The server's own cap — `@ArrayMaxSize(500)` on both `BulkAttachMenuItemsDto`
 * and `BulkDetachMenuItemsDto` (`dto/bulk-menu-items.dto.ts`), the same number
 * `ReorderCatalogDto` uses for the same reason: one request, one transaction,
 * a body a person can still read in a log.
 */
export const ITEMS_BULK_MAX = 500;

/** Split a list into requests no larger than the server will accept in one. */
export function chunk<T>(list: readonly T[], size: number = ITEMS_BULK_MAX): T[][] {
  if (list.length === 0) return [];
  const out: T[][] = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

/** One row of `POST /menus/:id/items/bulk` — `BulkMenuItemDto`. Both fields
 *  are optional there (default `sortOrder: 0`, `isVisible: true` on create,
 *  "leave unchanged" on an existing row), so a full `AttachItemBody` and a
 *  partial `ItemUpdate` are both valid rows without conversion. */
export interface BulkItemBody {
  itemId: string;
  sortOrder?: number;
  isVisible?: boolean;
}

/**
 * Whether the review step may promise the menu reaches a guest.
 *
 * The design's banner says "visible to guests immediately after creation" flat
 * out. It is not reliably true: `resolveActiveMenu` returns exactly one menu
 * per guest request ordered `isDefault desc, createdAt asc`, so a new
 * non-default branch menu created after an existing one loses the pick; an
 * inactive menu is never resolved; and a menu with no items resolves to an
 * empty section list. The banner reports which of those applies instead of
 * asserting the happy one.
 */
export type PublishVerdict = 'ready' | 'inactive' | 'noItems' | 'notDefault';

export function publishVerdict(draft: MenuDraft, otherMenusExist: boolean): PublishVerdict {
  if (!draft.isActive) return 'inactive';
  if (draft.items.length === 0) return 'noItems';
  if (!draft.isDefault && otherMenusExist) return 'notDefault';
  return 'ready';
}

/**
 * Whether the rows *currently shown* by the picker are all, partly, or not
 * selected.
 *
 * Shown, not the whole catalogue. The picker has a search box and category
 * chips and this tenant's catalogue runs to hundreds of items; a control that
 * reported — or acted on — the unfiltered set would be selecting things the
 * operator cannot see. An empty shown list is `none`: there is nothing to
 * select, so the box is unticked and the button does nothing.
 */
export type ShownSelection = 'none' | 'some' | 'all';

export function selectionOfShown(shownIds: string[], selected: DraftItem[]): ShownSelection {
  if (shownIds.length === 0) return 'none';
  const chosen = new Set(selected.map((row) => row.itemId));
  const hits = shownIds.filter((id) => chosen.has(id)).length;
  if (hits === 0) return 'none';
  return hits === shownIds.length ? 'all' : 'some';
}

/**
 * Select or clear every shown row, leaving everything else where it was.
 *
 * Selecting **appends** the missing rows in the order they are shown, and never
 * touches a row that is already selected. That is what keeps the edit diff
 * sane: the rows already on the menu keep their positions, so `itemDiff`
 * produces attaches for the new ones and no reorder for the rest. Re-adding
 * them at the front would have turned "select all" into an N-row reorder — N
 * audited PATCHes for a click that added nothing.
 *
 * Clearing removes only the shown rows, so clearing a filtered view does not
 * quietly empty the rest of the menu.
 */
export function toggleAllShown(
  selected: DraftItem[],
  shownIds: string[],
  on: boolean,
): DraftItem[] {
  const shown = new Set(shownIds);
  if (!on) return selected.filter((row) => !shown.has(row.itemId));

  const chosen = new Set(selected.map((row) => row.itemId));
  const added = shownIds
    .filter((id) => !chosen.has(id))
    .map((itemId) => ({ itemId, isVisible: true }));
  return added.length ? [...selected, ...added] : selected;
}

/** Moves `id` to sit where `overId` currently is, preserving everything else. */
export function reorderItems(items: DraftItem[], id: string, overId: string): DraftItem[] {
  const from = items.findIndex((row) => row.itemId === id);
  const to = items.findIndex((row) => row.itemId === overId);
  if (from < 0 || to < 0 || from === to) return items;
  const next = items.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/* ------------------------------------------------------------------ *
 * Editing an existing menu
 * ------------------------------------------------------------------ */

/**
 * One attached row as the *server* currently holds it.
 *
 * Deliberately not a `DraftItem`: the draft is an ordered list whose index is
 * the `sortOrder` it intends to write, while the server holds an explicit
 * `sortOrder` that need not be contiguous — the menu detail card lets an
 * operator type `7` into a three-row menu. The diff compares the two, so both
 * shapes have to exist.
 */
export interface SavedItem {
  itemId: string;
  sortOrder: number;
  isVisible: boolean;
}

/** What `GET /menus/:id` said, in the terms the wizard reasons about. */
export interface MenuSnapshot {
  name: string;
  /** `null` is the tenant-wide menu, not "unset". */
  branchId: string | null;
  isActive: boolean;
  isDefault: boolean;
  schedule: ScheduleWindow[];
  items: SavedItem[];
}

/**
 * The snapshot, from the one call that already returns everything.
 *
 * `MenusService.findOne` includes `items` ordered by `sortOrder asc` alongside
 * the menu and its branch, so seeding an edit needs no second request — the
 * schedule and the attached rows arrive with the menu. The sort is repeated
 * here anyway because this function is pure and will happily be handed a menu
 * that came from somewhere else.
 */
export function snapshotOf(menu: Menu): MenuSnapshot {
  return {
    name: menu.name,
    branchId: menu.branchId ?? null,
    isActive: menu.isActive,
    isDefault: menu.isDefault,
    schedule: Array.isArray(menu.schedule) ? menu.schedule : [],
    items: (menu.items ?? [])
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((row) => ({ itemId: row.itemId, sortOrder: row.sortOrder, isVisible: row.isVisible })),
  };
}

/**
 * The draft the four steps start from when the menu already exists.
 *
 * `alwaysAvailable` is derived rather than read, for the same reason it is
 * derived on create: there is no such column. An empty or absent `schedule` is
 * what `isScheduleOpen` reads as always-open, so a stored `[]` seeds the toggle
 * ON — and the operator who turns it off gets an empty window list, not a
 * resurrected one, because there is nothing stored to resurrect.
 */
export function draftFromSnapshot(saved: MenuSnapshot): MenuDraft {
  return {
    name: saved.name,
    scope: saved.branchId ? 'branch' : 'tenant',
    branchId: saved.branchId ?? '',
    isActive: saved.isActive,
    isDefault: saved.isDefault,
    alwaysAvailable: saved.schedule.length === 0,
    schedule: saved.schedule,
    items: saved.items.map((row) => ({ itemId: row.itemId, isVisible: row.isVisible })),
  };
}

/** The windows as they will be stored — the toggle collapses to `[]`. */
function effectiveSchedule(draft: MenuDraft): ScheduleWindow[] {
  return draft.alwaysAvailable ? [] : draft.schedule;
}

function sameSchedule(a: ScheduleWindow[], b: ScheduleWindow[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((window, index) => {
    const other = b[index];
    return (
      window.from === other.from &&
      window.to === other.to &&
      window.days.length === other.days.length &&
      window.days.every((day, i) => day === other.days[i])
    );
  });
}

/**
 * `UpdateMenuDto extends PartialType(CreateMenuDto)` — the same five keys, each
 * optional (`update-menu.dto.ts:4`). Same list, separate constant, because they
 * are separate DTOs that are free to diverge and a shared alias would hide the
 * day one of them does.
 */
export const UPDATE_MENU_FIELDS = ['name', 'branchId', 'schedule', 'isDefault', 'isActive'] as const;

export interface UpdateMenuBody {
  name: string;
  /** Always present, and `null` for tenant-wide — see below. */
  branchId: string | null;
  schedule: ScheduleWindow[];
  isDefault: boolean;
  isActive: boolean;
}

/**
 * The draft as `PATCH /menus/:id` will take it.
 *
 * The one real difference from `createMenuBody`, and the bug it fixes:
 * **`branchId` is always sent, as `null` when the menu is tenant-wide.** The
 * dialog this replaced sent `branchId: form.branchId || undefined`, and
 * `undefined` does not survive `JSON.stringify` — the key was dropped, the
 * PATCH left `branchId` untouched, and converting a branch menu to the
 * tenant-wide fallback silently did nothing. `null` is accepted: `@IsOptional()`
 * skips its validators for `null` as well as `undefined`, `branchId` is a
 * declared property so `forbidNonWhitelisted` keeps it, `main.ts` does not set
 * `skipNullProperties`, and `MenusService.update` spreads it straight into
 * `prisma.menu.update` where `Menu.branchId` is nullable.
 *
 * `schedule` is likewise always sent, including as `[]`: omitting it on an edit
 * would leave the previous windows in place, which is the opposite of what
 * turning "always available" on means.
 */
export function updateMenuBody(draft: MenuDraft): UpdateMenuBody {
  return {
    name: draft.name.trim(),
    branchId: draft.scope === 'branch' && draft.branchId ? draft.branchId : null,
    schedule: effectiveSchedule(draft),
    isDefault: draft.isDefault,
    isActive: draft.isActive,
  };
}

/** One `PATCH /menus/:id/items/:itemId` body — `UpdateMenuItemDto`, both keys
 *  optional, so only the ones that actually moved are sent. */
export interface ItemUpdate {
  itemId: string;
  sortOrder?: number;
  isVisible?: boolean;
}

/**
 * The three lists an edit turns into — what gets attached, what gets moved or
 * re-hidden without changing which menu it is on, and what comes off.
 *
 * `menus.controller.ts` now has `POST :id/items/bulk` and `DELETE
 * :id/items/bulk` alongside the single-row routes, so none of this ships as
 * one request per row any more (`itemBatches`, below, turns it into the small
 * number of bulk calls it actually costs). The three-way split survives
 * because it is still what a failure is keyed by: a row appears in at most one
 * list, so `rebaseSaved` can say exactly which ones a refused batch left
 * undone.
 */
export interface ItemDiff {
  attach: AttachItemBody[];
  update: ItemUpdate[];
  detach: string[];
}

/**
 * What changed between the rows the server holds and the list the operator built.
 *
 * A row appears in at most one of the three lists, which is what lets a failure
 * be keyed by `itemId` alone. Note that a row whose position did not visibly
 * move can still land in `update`: the wizard normalises `sortOrder` to the
 * list index, so stored orders of `0, 5, 9` become `0, 1, 2` and two rows are
 * genuinely rewritten.
 */
export function itemDiff(before: SavedItem[], after: DraftItem[]): ItemDiff {
  const previous = new Map(before.map((row) => [row.itemId, row]));
  const target = new Set(after.map((row) => row.itemId));

  const attach: AttachItemBody[] = [];
  const update: ItemUpdate[] = [];

  after.forEach((row, sortOrder) => {
    const was = previous.get(row.itemId);
    if (!was) {
      attach.push({ itemId: row.itemId, sortOrder, isVisible: row.isVisible });
      return;
    }
    const patch: ItemUpdate = { itemId: row.itemId };
    if (was.sortOrder !== sortOrder) patch.sortOrder = sortOrder;
    if (was.isVisible !== row.isVisible) patch.isVisible = row.isVisible;
    if (patch.sortOrder !== undefined || patch.isVisible !== undefined) update.push(patch);
  });

  return {
    attach,
    update,
    detach: before.filter((row) => !target.has(row.itemId)).map((row) => row.itemId),
  };
}

/** How many item changes the edit costs — attaches, moves, hides and detaches
 *  combined. Not the request count any more: see `itemBatches` for that. */
export function diffSize(diff: ItemDiff): number {
  return diff.attach.length + diff.update.length + diff.detach.length;
}

/**
 * Everything the save writes through `POST /menus/:id/items/bulk`, combined.
 *
 * `MenusService.bulkAttachItems` upserts on `menuId_itemId`, so a brand-new
 * row and a reordered or re-hidden existing row are the identical write
 * server-side — `create` for one, `update` for the other, inside the same
 * transaction. Sending them as two separate request families would only be
 * two chances to hit the same throttle for a distinction the server does not
 * make.
 */
export function bulkAttachBodies(attach: AttachItemBody[], update: ItemUpdate[]): BulkItemBody[] {
  return [...attach, ...update];
}

/** One HTTP call an item save actually makes, and the ids riding in it — kept
 *  alongside the wire body so a failure can report "these ids did not land"
 *  without re-deriving them from `items`. */
export type ItemBatch =
  | { phase: 'detach'; itemIds: string[] }
  | { phase: 'attach'; itemIds: string[]; items: BulkItemBody[] };

/**
 * The diff, laid out as the ordered bulk requests it actually costs: every
 * detach chunk (freeing the positions the remaining rows may claim), then
 * every attach/reorder chunk, each no larger than `ITEMS_BULK_MAX`.
 *
 * This is the one function both `submitCreate` and `submitEdit` run their
 * item half through — a create has no detach and no update, so it is just
 * `itemBatches({ attach: attachItemBodies(draft), update: [], detach: [] })`,
 * the same chunking with two of the three lists empty.
 *
 * Each batch is exactly what `MenusService.bulkAttachItems` /
 * `bulkDetachItems` promise: one transaction, applied in full or not at all.
 * That is what makes a batch a sound unit of "still to do" — unlike the old
 * per-item loop, where a refusal mid-run said nothing about whether the
 * request that hit it had actually landed.
 */
export function itemBatches(diff: ItemDiff): ItemBatch[] {
  const detach: ItemBatch[] = chunk(diff.detach).map((itemIds) => ({
    phase: 'detach',
    itemIds,
  }));
  const attach: ItemBatch[] = chunk(bulkAttachBodies(diff.attach, diff.update)).map((items) => ({
    phase: 'attach',
    itemIds: items.map((item) => item.itemId),
    items,
  }));
  return [...detach, ...attach];
}

/**
 * The snapshot rebased onto whatever actually landed.
 *
 * This is the edit equivalent of create keeping the menu id after a refused
 * attach. Every op is keyed by `itemId` and at most one op exists per row, so
 * "apply everything except these ids" is exact — and re-running `itemDiff`
 * against the result yields precisely the refused work and nothing else. The
 * operator presses the same button again and re-sends only what was refused.
 */
export function rebaseSaved(before: SavedItem[], diff: ItemDiff, refused: string[]): SavedItem[] {
  const stuck = new Set(refused);
  const rows = new Map(before.map((row) => [row.itemId, { ...row }]));

  for (const body of diff.attach) {
    if (stuck.has(body.itemId)) continue;
    rows.set(body.itemId, { ...body });
  }
  for (const itemId of diff.detach) {
    if (!stuck.has(itemId)) rows.delete(itemId);
  }
  for (const patch of diff.update) {
    const row = rows.get(patch.itemId);
    if (!row || stuck.has(patch.itemId)) continue;
    if (patch.sortOrder !== undefined) row.sortOrder = patch.sortOrder;
    if (patch.isVisible !== undefined) row.isVisible = patch.isVisible;
  }

  return [...rows.values()].sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Which of the menu's own fields the PATCH would change.
 *
 * `items` is listed alongside them so one array answers "is there anything to
 * save at all", but it is filtered back out before the body is assembled: items
 * are not on `UpdateMenuDto` and never travel with the menu.
 */
export type MenuChange = 'name' | 'branch' | 'isDefault' | 'isActive' | 'schedule' | 'items';

export function menuChanges(before: MenuSnapshot, draft: MenuDraft, diff: ItemDiff): MenuChange[] {
  const changes: MenuChange[] = [];
  if (before.name !== draft.name.trim()) changes.push('name');
  if (before.branchId !== updateMenuBody(draft).branchId) changes.push('branch');
  if (before.isDefault !== draft.isDefault) changes.push('isDefault');
  if (before.isActive !== draft.isActive) changes.push('isActive');
  if (!sameSchedule(before.schedule, effectiveSchedule(draft))) changes.push('schedule');
  if (diffSize(diff) > 0) changes.push('items');
  return changes;
}

/** Everything except `items` — i.e. whether `PATCH /menus/:id` is worth sending
 *  at all. An items-only edit should not leave a `menu.update` audit row behind. */
export function menuFieldChanges(before: MenuSnapshot, draft: MenuDraft, diff: ItemDiff) {
  return menuChanges(before, draft, diff).filter((change) => change !== 'items');
}

/**
 * The changes that take something away, as codes.
 *
 * Adding to a draft is safe; an edit is not. Every one of these names a
 * consequence that lives in backend code rather than a field that changed:
 *
 * - `emptyingMenu` — every row goes. One press of Deselect all does this, and
 *   it is not the same sentence as "3 items will be detached": the menu is left
 *   resolving to an empty section list for every guest on it.
 * - `removingItems` — `detachItem` deletes the `MenuItem` row outright.
 * - `deactivating` — `resolveActiveMenu` filters `isActive: true`, so the menu
 *   stops resolving for every guest the moment this lands.
 * - `losingDefault` — nothing re-elects a default. Clearing the flag can leave
 *   the tenant with none, and the guest then gets whichever menu sorts first by
 *   `createdAt`. Same consequence the delete confirmation already warns about.
 * - `becomingDefault` — `clearOtherDefaults` un-defaults every sibling menu in
 *   the same branch scope. That is a write to rows the operator did not open.
 * - `losingTenantFallback` — a tenant-wide menu is the `branchId: null` row
 *   `resolveActiveMenu` falls back to when no branch menu matches. Scoping it to
 *   a branch removes that fallback for every other branch.
 * - `narrowingSchedule` — an always-open menu that gains windows is closed
 *   outside them, and `isScheduleOpen` reads the windows on the branch's own
 *   wall clock, not the operator's.
 */
export type EditWarning =
  | 'emptyingMenu'
  | 'removingItems'
  | 'deactivating'
  | 'losingDefault'
  | 'becomingDefault'
  | 'losingTenantFallback'
  | 'narrowingSchedule';

export function editWarnings(
  before: MenuSnapshot,
  draft: MenuDraft,
  diff: ItemDiff,
): EditWarning[] {
  const warnings: EditWarning[] = [];
  // One or the other, never both — the stronger sentence says everything the
  // weaker one would have.
  if (diff.detach.length > 0 && draft.items.length === 0) warnings.push('emptyingMenu');
  else if (diff.detach.length > 0) warnings.push('removingItems');
  if (before.isActive && !draft.isActive) warnings.push('deactivating');
  if (before.isDefault && !draft.isDefault) warnings.push('losingDefault');
  if (!before.isDefault && draft.isDefault) warnings.push('becomingDefault');
  if (before.branchId === null && updateMenuBody(draft).branchId !== null)
    warnings.push('losingTenantFallback');
  if (before.schedule.length === 0 && effectiveSchedule(draft).length > 0)
    warnings.push('narrowingSchedule');
  return warnings;
}
